/*
We could probably base this on the new ListViewTable component, but it should not be done in this PR
 */
import { Button, Flex, Icon, TableColumn, TableRow, Text } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import { showCoverup } from 'App/AngularApp';
import ListView, { ListViewPropsExternal } from 'App/components/ListView';
import {
	ListViewDefaultColumn,
	renderDefaultTable,
	RenderTableProvided,
	RenderTableRowProvided
} from 'App/components/ListView/ListViewRenderHelpers';
import React, { Fragment, useEffect, useState } from 'react';
import './SimpleListView.scss';
import { ListViewTableProvided } from 'App/components/ListView/ListViewTable/ListViewTable';
import { getStandardFieldsEntity } from 'App/components/ListView/ListView';
import Attribute, { Attr, Type } from 'App/babel/attributes/Attribute';
import { filterActiveAttributes } from 'App/babel/attributes/attributeHelpers';
import InlineConfirm from 'Components/Dialogs/InlineConfirm';
import T from 'Components/Helpers/translate';
import LocalStorage from 'Components/Helpers/LocalStorage';
import AppService from 'App/legacy/AppService';
import openModal from 'App/services/Modal';

type TableRowProvided<ItemType extends {}> = ListViewTableProvided<ItemType, RenderTableRowProvided<ItemType>>;

type Props<ItemType extends {}> = {
	columns: string[];
	getData: ListViewPropsExternal<ItemType>['getData'];
	subHeaderFn?: (item: ItemType, sorting: string) => string;
	/**
	 * Rendered after the sub-header text, from every loaded row that shares that sub-header.
	 *
	 * `subHeaderFn` can only return a string, so a group total (the Angular cards showed
	 * `2026 (13 390 SEK)`) had nowhere to go. Aggregating here rather than widening
	 * `subHeaderFn` keeps the string as the grouping identity - it is what decides when a new
	 * sub-header starts.
	 *
	 * Only the current page is summed, so pair it with a `tableLimitOptions` that fits the data.
	 */
	subHeaderAggregateFn?: (itemsInGroup: ItemType[]) => React.ReactNode;
	rowStyles?: { [modName: string]: (item: ItemType) => boolean };
	onRowClick?: (item: ItemType, provided: TableRowProvided<ItemType>) => () => void;
	classes?: BemClass;
	hideNoResults?: boolean;
	/** Supply custom render functions for columns. By default columns will be rendered by `ListViewDefaultColumn`, based on `attributes`,
	 * but this can be used to override that behavior with your own components */
	customColumnRenders?: {
		[column: string]: (item: ItemType, provided: TableRowProvided<ItemType>) => React.ReactNode;
	};
	/** @deprecated Prefer using `customColumnRenders`, `rowStyles` and `onRowClick` props instead of defining your own row renderer */
	renderTableRow?: ListViewPropsExternal<ItemType>['renderTableRow'];
	/** Pickable columns are the non-fixed `columns` plus `selectableColumns`; `fixedColumns` are always shown last. */
	columnPicker?: { storageKey: string; tableType: string; selectableColumns: string[]; fixedColumns?: string[] };
} & OptionalPropGroup<{
	deleteFn: (item: ItemType, provided: TableRowProvided<ItemType>) => Promise<void>;
	isDeletable: (item: ItemType) => boolean;
	entityName: string;
	useHeader?: boolean;
	noStickyHeader?: boolean;
}> &
	PartialOmit<ListViewPropsExternal<ItemType>, 'renderTableRow'>;

const modifyRenderTableFn =
	<ItemType extends {}>(
		renderTable: Required<Props<ItemType>>['renderTable'],
		getSubHeader: Required<Props<ItemType>>['subHeaderFn'],
		getSubHeaderAggregate?: Props<ItemType>['subHeaderAggregateFn']
	) =>
	(tableProvided: RenderTableProvided<ItemType>) => {
		const lastHeader = { current: '', index: 0 };

		const modifiedRenderRow = (
			item: ItemType,
			rowProvided: ListViewTableProvided<ItemType, RenderTableRowProvided<ItemType>>
		) => {
			// Attach a sub-header row above tableRows if the text to be displayed has changed.
			// Relies on being synced up with sorting - aka it only checks if the item before is different
			// Consider: May want to disconnect the rendering itself from the logic deciding if it should render. Current
			// approach does not allow for any more advanced rendering than just straight text
			const sorting = tableProvided.sorting[0]?.attribute ?? '';
			const headerText = getSubHeader(item, sorting);
			const shouldDisplayHeader = headerText && headerText !== lastHeader.current;
			lastHeader.current = headerText;
			const aggregate = getSubHeaderAggregate
				? getSubHeaderAggregate(
						tableProvided.tableData.filter(row => getSubHeader(row, sorting) === headerText)
				  )
				: null;
			return shouldDisplayHeader ? (
				<Fragment key={`${item[rowProvided.itemIdentifier]}`}>
					<TableRow
						key={`subHeader${lastHeader.index++}`}
						className={rowProvided.classes.elem('subHeader').b()}
					>
						<TableColumn colSpan={100}>
							<Text bold size="lg">
								{headerText}
							</Text>
							{aggregate}
						</TableColumn>
					</TableRow>
					{tableProvided.renderTableRow(item, rowProvided)}
				</Fragment>
			) : (
				tableProvided.renderTableRow(item, rowProvided)
			);
		};

		return renderTable({ ...tableProvided, renderTableRow: modifiedRenderRow });
	};

type TableRowProps<ItemType extends {}> = {
	item: ItemType;
	provided: TableRowProvided<ItemType>;
} & Pick<Props<ItemType>, 'customColumnRenders' | 'rowStyles' | 'onRowClick'>;

const SimpleListViewTableRow = <ItemType extends {}>({
	item,
	provided,
	customColumnRenders = {},
	rowStyles = {},
	onRowClick
}: TableRowProps<ItemType>) => {
	const { attributes, columns, itemIdentifier, customFields, categories } = provided;

	const classes = new BemClass('TableRow');

	Object.keys(rowStyles).forEach(modName => {
		if (rowStyles[modName](item)) {
			classes.mod(modName);
		}
	});

	const columnElements = columns.map(column =>
		customColumnRenders[column] ? (
			<TableColumn key={column} className={classes.elem(column).b()}>
				{customColumnRenders[column](item, provided)}
			</TableColumn>
		) : (
			<ListViewDefaultColumn<ItemType>
				key={column}
				column={column}
				attributes={attributes}
				item={item}
				customFields={customFields}
				categories={categories}
			/>
		)
	);

	if (provided.renderToolsColumn) {
		columnElements.push(<TableColumn key="tools-column" />);
	}

	return (
		<TableRow key={item[itemIdentifier] as any} className={classes.b()} onClick={onRowClick?.(item, provided)}>
			{columnElements}
		</TableRow>
	);
};

export const InlineDeleteButton = <ItemType extends {}>({
	isDeletable,
	deleteFn,
	entityName,
	item,
	provided
}: Required<Pick<Props<ItemType>, 'isDeletable' | 'deleteFn' | 'entityName'>> & {
	item: ItemType;
	provided: TableRowProvided<ItemType>;
}) => (
	<Flex flex={[0, 0, '40px']}>
		<InlineConfirm
			show={isDeletable(item)}
			tooltip={T('default.delete')}
			onConfirm={() => {
				return deleteFn(item, provided).finally(() => {
					setTimeout(() => provided.reloadTable({ silent: true }), 1000);
				});
			}}
			entity={entityName}
		>
			<Button type="link" color="grey">
				<Icon name="trash" />
			</Button>
		</InlineConfirm>
	</Flex>
);

const SimpleListView = <ItemType extends {}>({
	classes,
	columns,
	getData,
	subHeaderFn,
	subHeaderAggregateFn,
	rowStyles,
	onRowClick,
	renderTable,
	renderTableRow,
	hideNoResults,
	deleteFn,
	isDeletable,
	entityName,
	customColumnRenders = {},
	useHeader,
	columnPicker,
	...passthrough
}: Props<ItemType>) => {
	if (classes) {
		classes.add('SimpleListView');
	} else {
		classes = new BemClass('SimpleListView');
	}
	const [hasNoData, setHasNoData] = useState(false);
	const storageKey = columnPicker ? `simpleListView.${columnPicker.storageKey}.columns` : null;
	const [pickedColumns, setPickedColumns] = useState(() =>
		storageKey ? new LocalStorage().getValue<string[]>(storageKey) : null
	);
	useEffect(() => {
		// Listview disables the coverup on mount - we don't want that here
		showCoverup();
	}, []);

	if (subHeaderFn) {
		renderTable = renderTable ?? renderDefaultTable;
		renderTable = modifyRenderTableFn(renderTable, subHeaderFn, subHeaderAggregateFn);
	}

	if (isDeletable && deleteFn) {
		customColumnRenders.delete = (item, provided) => {
			return (
				<Flex justifyContent="end">
					<InlineDeleteButton
						isDeletable={isDeletable}
						deleteFn={deleteFn}
						entityName={entityName!}
						item={item}
						provided={provided}
					/>
				</Flex>
			);
		};
	}

	renderTableRow =
		renderTableRow ??
		((item, provided) => (
			<SimpleListViewTableRow
				item={item}
				provided={provided}
				rowStyles={rowStyles}
				onRowClick={onRowClick}
				customColumnRenders={customColumnRenders}
			/>
		));

	const attributes: Record<string, Attr> = {
		delete: Attribute({
			title: '',
			field: '',
			type: Type.String
		}),
		...(passthrough.attributes ?? {})
	};

	const fixedColumns = columnPicker?.fixedColumns ?? [];
	const pickable = [
		...new Set([
			...columns.filter(column => !fixedColumns.includes(column)),
			...(columnPicker?.selectableColumns ?? [])
		])
	].filter(column => attributes[column]);
	const visibleColumns = pickedColumns
		? [...pickedColumns.filter(column => pickable.includes(column)), ...fixedColumns]
		: columns;

	if (columnPicker) {
		const { tableType } = columnPicker;
		const openColumnPicker = () => {
			const standardFields = AppService.getMetadata()?.standardFields[getStandardFieldsEntity(tableType)] ?? {};
			const active = filterActiveAttributes(
				Object.fromEntries(pickable.map(column => [column, attributes[column]])),
				standardFields
			);
			const selectables = Object.keys(active).map(key => ({
				key,
				field: active[key].field,
				title: active[key].title
			}));
			openModal('EditColumnsModal', {
				attributes,
				columns: visibleColumns.filter(column => !fixedColumns.includes(column)),
				selectables,
				tableType,
				onClose: columns => {
					if (!columns) return;
					new LocalStorage().setValue(storageKey!, columns);
					setPickedColumns(columns);
				}
			});
		};
		const renderTableWithoutPicker = renderTable ?? renderDefaultTable;
		renderTable = provided => renderTableWithoutPicker({ ...provided, editColumns: openColumnPicker });
	}

	return !(hideNoResults && hasNoData) ? (
		<ListView<ItemType>
			noStickyTableHeader
			className={classes.b()}
			getData={(rb, provided) =>
				getData(rb, provided).then(a => {
					if (!a.data.length) {
						setHasNoData(true);
					}
					return a;
				})
			}
			onChange={prov => prov.reloadTable({ silent: true })}
			isFullscreen={false}
			columns={visibleColumns}
			renderToolsColumn={!!columnPicker}
			skipSortById
			renderHeader={useHeader ? undefined : () => <></>}
			tableLimitOptions={[10]}
			renderTable={renderTable}
			renderTableRow={renderTableRow}
			{...passthrough}
			attributes={attributes}
		/>
	) : null;
};

export default SimpleListView;
